import React from 'react';

import { useUpdateEffect } from '@private/hooks';

import { appReactMemo } from 'hocs';

import { ContentContainer, Header } from './styled';

export interface IAccordionHeaderProps {
  contentHeight: null | number;
  isOpen: boolean;
  openHandler: (state?: boolean) => void;
}

export interface IAccordionProps {
  children?: ((props: IAccordionHeaderProps) => React.ReactNode) | React.ReactNode;
  className?: string;
  disableAutoHandle?: boolean;
  header?: ((props: IAccordionHeaderProps) => React.ReactNode) | React.ReactNode;
  isInitialOpen?: boolean;
  isOpen?: boolean;
  onClick?: (props: { isOpen: boolean }) => void;
}

const Accordion: React.FC<IAccordionProps> = ({
  children,
  className,
  disableAutoHandle,
  header,
  isInitialOpen = false,
  isOpen = false,
  onClick,
}) => {
  const [open, setOpen] = React.useState(isInitialOpen);

  // null = settled (height: auto). A number pins the height in px only while the
  // open/close transition runs — pinning permanently would force every ancestor
  // to track descendant size changes with observers, which is what used to make
  // nested accordions re-render whole subtrees on every animation frame.
  const [height, setHeight] = React.useState<null | number>(isInitialOpen ? null : 0);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const hasOpen = React.useRef(isInitialOpen);
  // Last measured content height, so header render-props keep receiving a truthy
  // contentHeight after the style has settled to auto.
  const measuredHeight = React.useRef<null | number>(null);

  const openHandler = React.useCallback((newState: boolean) => {
    if (newState && !hasOpen.current) {
      hasOpen.current = true;
    }
    setOpen(newState);
  }, []);

  const onCustomHeaderOpen = React.useCallback(
    (newState?: boolean) => {
      openHandler(newState === undefined ? !open : newState);
    },
    [open, openHandler],
  );

  const onHeaderHandler = React.useCallback(() => {
    if (disableAutoHandle) return;
    openHandler(!open);
  }, [open, openHandler, disableAutoHandle]);

  React.useEffect(() => {
    onClick?.({ isOpen: open });
  }, [open]);

  useUpdateEffect(() => {
    openHandler(isOpen);
  }, [isOpen]);

  const prevOpen = React.useRef(open);
  React.useLayoutEffect(() => {
    if (prevOpen.current === open) return;
    prevOpen.current = open;

    const node = containerRef.current;
    if (!node) return;

    if (open) {
      // Flush the collapsed height so the transition has a concrete starting
      // value — freshly mounted content would otherwise paint directly at the
      // target height with no animation.
      node.getBoundingClientRect();

      const target = contentRef.current?.scrollHeight ?? 0;
      measuredHeight.current = target;

      // Equal start and end means no transition and no transitionend to settle
      // us, so settle straight to auto.
      setHeight(target === node.clientHeight ? null : target);
      return;
    }

    if (height === null) {
      // height: auto can't be transitioned from — pin the current pixel height
      // and flush it so the collapse animates instead of jumping.
      node.style.height = `${node.clientHeight}px`;
      node.getBoundingClientRect();
    }
    setHeight(0);
  }, [open, height]);

  const onTransitionEnd = React.useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      // transitionend bubbles: ignore transitions of descendants (tab
      // backgrounds, input borders, ...) and of properties other than height.
      if (e.target !== e.currentTarget || e.propertyName !== 'height') return;

      if (open) {
        setHeight(null);
      }
    },
    [open],
  );

  const renderProps = {
    contentHeight: open ? (height ?? measuredHeight.current) : 0,
    isOpen: open,
    openHandler: onCustomHeaderOpen,
  };

  return (
    <div className={className} data-is-open={open}>
      {typeof header === 'function' ? (
        header(renderProps)
      ) : (
        <Header onClick={onHeaderHandler}>{header}</Header>
      )}

      {hasOpen.current && (
        <ContentContainer
          $isSettled={open && height === null}
          onTransitionEnd={onTransitionEnd}
          ref={containerRef}
          style={height === null ? undefined : { height: `${height}px` }}
        >
          <div ref={contentRef}>
            {typeof children === 'function' ? children(renderProps) : children}
          </div>
        </ContentContainer>
      )}
    </div>
  );
};

export default appReactMemo(Accordion);
